/*
 * Tiny fetch wrapper. Reads JWT from localStorage, attaches to Authorization header.
 * 401 → boots back to /login.
 */
const TOKEN_KEY = 'sa_token'
const USER_KEY = 'sa_user'

export interface AuthedUser { name: string; email: string; role: string }

export const auth = {
    token: () => localStorage.getItem(TOKEN_KEY),
    user: (): AuthedUser | null => {
        const raw = localStorage.getItem(USER_KEY)
        return raw ? JSON.parse(raw) : null
    },
    save(token: string, user: AuthedUser) {
        localStorage.setItem(TOKEN_KEY, token)
        localStorage.setItem(USER_KEY, JSON.stringify(user))
    },
    clear() {
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(USER_KEY)
    },
    isAdmin: () => auth.user()?.role === 'Admin'
}

class HttpError extends Error {
    constructor(public status: number, message: string, public body?: any) {
        super(message)
    }
}

export async function api<T = any>(path: string, opts: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(opts.headers as Record<string, string> | undefined ?? {})
    }
    const tok = auth.token()
    if (tok) headers['Authorization'] = `Bearer ${tok}`

    const res = await fetch(path, { ...opts, headers })

    if (res.status === 401) {
        auth.clear()
        if (!location.pathname.startsWith('/login') && !location.pathname.startsWith('/subscribe'))
            location.assign('/login')
        throw new HttpError(401, 'unauthorized')
    }

    if (res.status === 204) return undefined as T

    const text = await res.text()
    let body: any = null
    try { body = text ? JSON.parse(text) : null } catch { body = text }

    if (!res.ok) throw new HttpError(res.status, body?.error ?? `HTTP ${res.status}`, body)
    return body as T
}

export const fetcher = (path: string) => api(path)
